import { PropsWithChildren, ReactNode } from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';

import { Link, Route, useRouteMatch } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';

import { Avatar } from './Avatar';
import { Highlight } from './Highlight';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { Label, Paragraph } from './Text';

const Container = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlue};
	height: 100%;
`;

const Contents = styled(Box)<{ sticky?: boolean }>`
	position: ${(props) => (props.sticky ? 'sticky' : 'relative')};
	top: 0;
	height: auto;
`;

const Icon = styled(Box)``;

const Badge = styled(Box)``;

const Subtitle = styled(Label)``;

type SidebarStatus = 'active' | 'inactive';

const SidebarItemIndicator = styled(Box)<{ status?: SidebarStatus }>`
	width: 10px;
	height: 10px;
	border-radius: 5px;
	position: absolute;
	right: 14px;
	display: none;

	${(props) =>
		props.status &&
		`
			background-color: ${
				props.status === 'active' ? props.theme.colors.green : props.theme.colors.lightBlue
			};
			display: block;
	`}
`;

const SidebarItemContainer = styled(Flex)<{
	hover?: boolean;
	selected?: boolean;
	small?: boolean;
	overflow?: 'hidden';
}>`
	border-radius: 6px;
	font-size: 16px;
	min-height: 50px;
	align-items: center;
	cursor: pointer;
	color: ${(props) => props.theme.colors.slateBlue};
	margin-bottom: 2px;
	padding: 16px;
	transition: 0.1s ease-out all;
	position: relative;

	${(props) => props.overflow === 'hidden' && `white-space: nowrap;`}

	${(props) =>
		props.small &&
		`
		min-height: 40px;
		padding: 8px 12px;
	`}

	${(props) =>
		props.overflow === 'hidden' &&
		`
		white-space: nowrap;
		overflow: hidden;
	`}

	${Subtitle} {
		color: ${(props) => rgba(props.theme.colors.slateBlue, 0.75)};
	}

	${Icon} svg {
		width: 24px;
		height: 24px;
		display: block;
	}

	${(props) =>
		props.selected
			? `
		background: ${rgba(props.theme.colors.primary, 0.1)};
		color: ${props.theme.colors.primary};
		font-weight: bold;

        ${Subtitle} {
            color: ${rgba(props.theme.colors.primary, 0.75)};
            font-weight: normal;
        }

		${Icon} svg path {
			fill: ${props.theme.colors.primary};
		}
	`
			: `
		${Icon} svg path {
			fill: ${props.theme.colors.slateBlue};
		}

		${
			props.hover === true
				? `
			background: ${rgba(props.theme.colors.slateBlue, 0.1)};
		`
				: ''
		}
		${
			props.hover === undefined
				? `&:hover {
				background: ${rgba(props.theme.colors.slateBlue, 0.1)};
			}`
				: ''
		}
	`}
`;

export function Sidebar(
	props: PropsWithChildren<BoxProps> & {
		sticky?: boolean;
		p?: number;
		onMouseEnter?: () => void;
		onMouseLeave?: () => void;
	},
) {
	return (
		<Container {...props}>
			<Contents sticky={props.sticky} p={props.p ?? 3}>
				{props.children}
			</Contents>
		</Container>
	);
}

Sidebar.Custom = function SidebarItem(props: PropsWithChildren<{ small?: boolean }>) {
	return (
		<Flex px={props.small ? '12px' : 3} height={props.small ? 40 : 50} alignItems="center">
			{props.children}
		</Flex>
	);
};

Sidebar.Item = Skeleton(
	function SidebarItem(
		props: BoxProps & {
			hover?: boolean;
			icon?: React.ReactNode;
			badge?: string | number;
			title?: string;
			subtitle?: string;
			exact?: boolean;
			small?: boolean;
			loading?: boolean;
			children?: ((props: { selected: boolean }) => ReactNode) | ReactNode | ReactNode[];
			status?: SidebarStatus;
			onMouseEnter?: () => void;
			onMouseLeave?: () => void;
			overflow?: 'hidden';
			selected?: boolean;
		} & (
				| {
						path: string[] | string;
						target?: string;
				  }
				| {
						onClick: () => Promise<unknown> | void;
				  }
			),
	) {
		if ('onClick' in props) {
			return (
				<SidebarItemContainer
					{...props}
					hover={props.hover}
					small={props.small}
					status={props.status}
					overflow={props.overflow}
					selected={props.selected}
				>
					<Flex justifyContent="space-between" width="100%">
						<Flex alignItems="center" overflow="hidden">
							{props.icon && (
								<Icon mr={2} flexShrink={0} flexGrow={0}>
									{props.loading ? <Spinner /> : <>{props.icon}</>}
								</Icon>
							)}
							<Box>
								{props.title}
								{props.subtitle && <Subtitle>{props.subtitle}</Subtitle>}
							</Box>
							{typeof props.children === 'function'
								? props.children({ selected: false })
								: props.children}
							<SidebarItemIndicator status={props.status} />
						</Flex>
						{props.badge && (
							<Badge fontWeight={700} ml={2} opacity={props.badge === '—' ? 0.3 : 1.0}>
								{props.badge}
							</Badge>
						)}
					</Flex>
				</SidebarItemContainer>
			);
		}

		return (
			<Link to={Array.isArray(props.path) ? props.path[0] : props.path} target={props.target}>
				<Route path={props.path} exact={props.exact}>
					{({ match }) => (
						<SidebarItemContainer
							{...props}
							hover={props.hover}
							selected={!!match}
							small={props.small}
							overflow={props.overflow}
						>
							<Flex justifyContent="space-between" width="100%">
								<Flex alignItems="center" overflow="hidden" width="100%">
									{props.icon && (
										<Icon mr={2} flexShrink={0} flexGrow={0}>
											{props.loading ? <Spinner /> : <>{props.icon}</>}
										</Icon>
									)}
									<Box>
										{props.title}
										{props.subtitle && <Subtitle>{props.subtitle}</Subtitle>}
									</Box>
									{typeof props.children === 'function'
										? props.children({ selected: !!match })
										: props.children}
									<SidebarItemIndicator status={props.status} />
								</Flex>
								{props.badge && (
									<Box alignSelf="flex-end">
										<Badge fontWeight={700} ml={2} opacity={props.badge === '—' ? 0.3 : 1.0}>
											{props.badge}
										</Badge>
									</Box>
								)}
							</Flex>
						</SidebarItemContainer>
					)}
				</Route>
			</Link>
		);
	},
	function (props: { small?: boolean }) {
		return (
			<Box p={props.small ? '8px 12px' : 3} mb={1}>
				<Flex>
					<Skeleton.Box height={24} width={24} mr={2} />
					<Skeleton.Box height={24} flexGrow={1} />
				</Flex>
			</Box>
		);
	},
);

Sidebar.Spacing = styled(Box)<{ small?: boolean }>`
	margin-top: ${(props) => (props.small ? 8 : 16)}px;
	margin-bottom: ${(props) => (props.small ? 8 : 16)}px;
	width: 100%;
`;

Sidebar.Separator = styled(Box)`
	margin: 16px -16px;
	height: 1px;
	background: ${(props) => props.theme.colors.lightBlue};
`;
Sidebar.SectionTitle = styled(Paragraph)`
	font-weight: bold;
	font-size: 14px;
	padding-left: 14px;
`;

const UserContainer = styled(Link)<{ selected?: boolean }>`
	display: block;
	padding: 0px 2px;
	margin: 0px -2px;
	border-radius: 4px;

	${(props) =>
		props.selected
			? `
		background: ${rgba(props.theme.colors.primary, 0.1)};

        ${Paragraph} {
            color: ${props.theme.colors.primary};
            font-weight: bold;
        }
	`
			: `
        &:hover {
            background: ${rgba(props.theme.colors.lightBlue, 0.5)};
        }
    `}
`;

function User(props: { to: string; user: graphql.User; query: string }) {
	const match = !!useRouteMatch(props.to);

	return (
		<UserContainer selected={match} to={props.to}>
			<Flex py={1} alignItems="center">
				<Box flexGrow={0} flexShrink={0} mr={2}>
					<Avatar user={props.user} size={24} />
				</Box>
				<Paragraph flexGrow={1} flexShrink={1}>
					<Highlight query={props.query}>
						{props.user.firstName} {props.user.lastName}
					</Highlight>
				</Paragraph>
			</Flex>
		</UserContainer>
	);
}

function UserSkeleton() {
	return (
		<Flex py={1} alignItems="center">
			<Box flexGrow={0} flexShrink={0} mr={2}>
				<Avatar.Skeleton size={24} />
			</Box>
			<Paragraph.Skeleton lines={1} />
		</Flex>
	);
}

Sidebar.User = User;
User.Skeleton = UserSkeleton;
